<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">Overview</h1>
        <p class="console-page__placeholder">
          See the current operating picture across workspaces, runs, and recent learning signals.
        </p>
        <ConsolePollingStatus
          active
          :busy="refreshBusy"
          idle-label="Auto-refresh every 15s"
          busy-label="Refreshing overview..."
          tone="active"
        />
      </div>

      <div class="console-page__actions">
        <button type="button" class="button button--ghost" :disabled="loading" @click="loadData({ showLoading: true })">
          Refresh
        </button>
        <router-link :to="{ name: 'ConsoleJobCreate' }" class="button button--primary">
          Submit Request
        </router-link>
      </div>
    </div>

    <section class="console-panel">
      <div v-if="warnings.length" class="overview-alerts">
        <div
          v-for="warning in warnings"
          :key="warning"
          class="alert alert--error"
        >
          {{ warning }}
        </div>
      </div>

      <ConsoleListState
        :loading="loading"
        :error="error"
        :is-empty="false"
      >
        <div v-if="alerts.length" class="overview-alert-list">
          <router-link
            v-for="alert in alerts"
            :key="alert.id"
            :to="alert.link"
            class="overview-alert"
            :class="`overview-alert--${alert.tone}`"
          >
            <div>
              <h2 class="overview-alert__title">{{ alert.title }}</h2>
              <p class="overview-alert__copy">{{ alert.copy }}</p>
            </div>
            <span class="overview-alert__action">{{ alert.action }}</span>
          </router-link>
        </div>

        <div class="console-signal-strip overview-summary-strip">
          <router-link
            v-for="card in summaryCards"
            :key="card.label"
            :to="card.link"
            class="console-signal"
            :class="card.tone ? `console-signal--${card.tone}` : ''"
          >
            <span class="console-signal__label">{{ card.label }}</span>
            <span class="console-signal__value">{{ card.value }}</span>
            <span class="console-signal__note">{{ card.copy }}</span>
          </router-link>
        </div>

        <details class="console-module" open>
          <summary class="console-module__summary">
            Operator Queue
            <span class="console-module__count">{{ operatorAttentionJobs }}</span>
          </summary>
          <div class="overview-module__header">
            <p class="overview-module__copy">Runs currently waiting for operator action or progress decisions.</p>
            <router-link :to="{ name: 'ConsoleJobs' }" class="link-muted">Open runs</router-link>
          </div>

          <dl class="console-metric-list">
            <div v-for="item in queueMetrics" :key="item.label" class="console-metric">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </details>

        <details class="console-module" open>
          <summary class="console-module__summary">
            Learning Signals
            <span class="console-module__count">{{ recurringTopics.length }}</span>
          </summary>
          <div class="overview-module__header">
            <p class="overview-module__copy">Recent analytics signals for gaps, evidence, and mismatch risk.</p>
            <router-link :to="{ name: 'ConsoleAnalytics' }" class="link-muted">Open analytics</router-link>
          </div>

          <dl v-if="analyticsMetrics.length" class="console-metric-list">
            <div v-for="item in analyticsMetrics" :key="item.label" class="console-metric">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
          <p v-else class="overview-empty">Analytics data is not available yet.</p>

          <div v-if="recurringTopics.length" class="console-stream overview-topic-stream">
            <article
              v-for="topic in recurringTopics"
              :key="topic.topic"
              class="console-stream__item"
            >
              <div>
                <h3 class="console-stream__title">{{ topic.topic }}</h3>
                <p class="console-stream__meta">Seen in {{ topic.count }} {{ topic.count === 1 ? 'run' : 'runs' }}</p>
              </div>
              <div class="console-stream__aside">
                <span
                  v-for="gapType in topic.gap_types"
                  :key="gapType"
                  class="console-badge console-badge--neutral"
                >
                  {{ formatStatusLabel(gapType) }}
                </span>
              </div>
            </article>
          </div>
        </details>

        <details class="console-module" open>
          <summary class="console-module__summary">
            Recent Runs
            <span class="console-module__count">{{ recentJobs.length }}</span>
          </summary>
          <div class="overview-module__header">
            <p class="overview-module__copy">Latest completed or failed run activity from the overview summary feed.</p>
            <router-link :to="{ name: 'ConsoleJobs' }" class="link-muted">Open runs</router-link>
          </div>

          <div v-if="recentJobs.length" class="console-stream">
            <router-link
              v-for="job in recentJobs"
              :key="job.jobId"
              :to="{ name: 'ConsoleJobDetail', params: { id: job.jobId } }"
              class="console-stream__item overview-stream__item"
              :class="`overview-stream__item--${job.tone}`"
            >
              <div>
                <h3 class="console-stream__title">{{ summarizeJobTitle(job.description, job.jobId, 72) }}</h3>
                <p class="console-stream__meta">
                  {{ getWorkspaceLabel(job.workspaceId) }}
                  <span v-if="job.runtime"> · {{ job.runtime }}</span>
                </p>
              </div>
              <div class="console-stream__aside">
                <span class="console-badge" :class="`console-badge--${job.tone}`">{{ job.statusLabel }}</span>
                <span class="console-stream__meta overview-stream__time">{{ formatRelativeTime(job.updatedAt) }}</span>
              </div>
            </router-link>
          </div>
          <p v-else class="overview-empty">No recent runs found yet.</p>
        </details>

        <details class="console-module" open>
          <summary class="console-module__summary">
            Recent Workspaces
            <span class="console-module__count">{{ recentWorkspaces.length }}</span>
          </summary>
          <div class="overview-module__header">
            <p class="overview-module__copy">Newest workspaces and the run load currently attached to them.</p>
            <router-link :to="{ name: 'ConsoleWorkspaces' }" class="link-muted">Open workspaces</router-link>
          </div>

          <div v-if="recentWorkspaces.length" class="console-stream">
            <router-link
              v-for="workspace in recentWorkspaces"
              :key="workspace.workspaceId"
              :to="{ name: 'ConsoleWorkspaceDetail', params: { id: workspace.workspaceId } }"
              class="console-stream__item overview-stream__item"
            >
              <div>
                <h3 class="console-stream__title">{{ workspace.name || workspace.workspaceId }}</h3>
                <p class="console-stream__meta">
                  {{ workspace.jobSummary?.total || 0 }} run{{ (workspace.jobSummary?.total || 0) === 1 ? '' : 's' }}
                  <span v-if="workspace.jobSummary?.failed"> · {{ workspace.jobSummary.failed }} failed</span>
                </p>
              </div>
              <div class="console-stream__aside">
                <span class="console-badge" :class="`console-badge--${workspace.status || 'ready'}`">
                  {{ workspace.status || 'ready' }}
                </span>
                <span class="console-badge console-badge--neutral">{{ workspace.sourceLabel }}</span>
                <span class="console-stream__meta overview-stream__time">Created {{ formatRelativeTime(workspace.createdAt) }}</span>
              </div>
            </router-link>
          </div>
          <p v-else class="overview-empty">No workspaces found yet.</p>
        </details>
      </ConsoleListState>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import ConsoleListState from '@/components/ConsoleListState.vue'
import ConsolePollingStatus from '@/components/ConsolePollingStatus.vue'
import {
  formatJobStatus,
  needsOperatorAttention,
  summarizeJobTitle,
} from '@/composables/useJobPresentation.js'
import { formatRelativeTime } from '@/composables/useRelativeTime.js'
import { useOrchestratorRequest } from '@/composables/useOrchestratorRequest.js'
import { useFrontendLogger } from '@/services/FrontendLogger'
import { fetchOverviewSummary } from '@/services/overviewSummary.js'
import { fetchWorkspaceSummaryPage } from '@/services/workspaceSummary.js'
import { fetchWorkspaceOptions } from '@/services/workspaceOptions.js'
import { fetchAllJobSummaryItems } from '@/services/jobSummary.js'

const { requestJson } = useOrchestratorRequest()
const logger = useFrontendLogger().child('[ConsoleOverview]')
const REFRESH_INTERVAL_MS = 15 * 1000

const loading = ref(true)
const error = ref('')
const warnings = ref([])
const overviewSummary = ref({
  workspaceCount: 0,
  activeJobs: 0,
  pendingPlans: 0,
  runningBeads: 0,
  failedJobs: 0,
  workspaces: {
    total: 0,
    visible: 0,
    byStatus: { creating: 0, ready: 0, failed: 0, archived: 0, deleting: 0 },
    bySource: { github: 0, upload: 0 },
  },
})
const recentJobs = ref([])
const activeQueueJobs = ref([])
const analytics = ref(null)
const stalePlans = ref([])
const recentWorkspaces = ref([])
const workspaceOptions = ref([])
const refreshBusy = ref(false)
let refreshInterval = null

const workspaceMap = computed(() => {
  const labels = new Map()

  for (const workspace of workspaceOptions.value) {
    labels.set(workspace.workspaceId, workspace.name || workspace.workspaceId)
  }

  for (const workspace of recentWorkspaces.value) {
    labels.set(workspace.workspaceId, workspace.name || workspace.workspaceId)
  }

  return labels
})

const operatorAttentionJobs = computed(() => activeQueueJobs.value.filter((job) => needsOperatorAttention(job.status)).length)

const queueMetrics = computed(() => {
  const awaitingInput = activeQueueJobs.value.filter((job) => job.status === 'awaiting_input').length
  const pendingApproval = activeQueueJobs.value.filter((job) => job.status === 'pending_approval').length
  const waitingCredentials = activeQueueJobs.value.filter((job) => job.status === 'waiting_credentials').length
  const approved = activeQueueJobs.value.filter((job) => job.status === 'approved').length

  return [
    { label: 'Awaiting input', value: awaitingInput },
    { label: 'Pending approval', value: pendingApproval },
    { label: 'Waiting credentials', value: waitingCredentials },
    { label: 'Approved to run', value: approved },
  ]
})

const analyticsMetrics = computed(() => {
  if (!analytics.value) return []

  return [
    { label: 'Runs with analytics', value: analytics.value.scope.jobs_with_analytics },
    { label: 'Runs with gaps', value: analytics.value.guidance_gaps.jobs_with_gaps },
    { label: 'Mismatch hotspots', value: analytics.value.mismatch_hotspots.total_mismatches },
    { label: 'Runtime-evidenced runs', value: analytics.value.evidence_coverage.jobs_with_runtime_evidence },
  ]
})

const recurringTopics = computed(() => {
  return analytics.value?.guidance_gaps?.recurring_topics?.slice(0, 3) ?? []
})

const summaryCards = computed(() => {
  const jobsWithGaps = analytics.value?.guidance_gaps?.jobs_with_gaps ?? 0
  const jobsWithAnalytics = analytics.value?.scope?.jobs_with_analytics ?? 0

  return [
    {
      label: 'Workspaces',
      value: overviewSummary.value.workspaceCount,
      copy: `${overviewSummary.value.workspaces.byStatus.failed} failed - ${overviewSummary.value.workspaces.bySource.github} on GitHub`,
      link: { name: 'ConsoleWorkspaces' },
      tone: overviewSummary.value.workspaces.byStatus.failed > 0 ? 'attention' : 'ready',
    },
    {
      label: 'Active Runs',
      value: overviewSummary.value.activeJobs,
      copy: `${overviewSummary.value.runningBeads} running step${overviewSummary.value.runningBeads === 1 ? '' : 's'} in flight`,
      link: { name: 'ConsoleJobs' },
      tone: overviewSummary.value.activeJobs > 0 ? 'active' : 'pending',
    },
    {
      label: 'Pending Plans',
      value: overviewSummary.value.pendingPlans,
      copy: 'Operator decisions queued for review',
      link: { name: 'ConsolePlans' },
      tone: overviewSummary.value.pendingPlans > 0 ? 'attention' : 'ready',
    },
    {
      label: 'Failed Runs',
      value: overviewSummary.value.failedJobs,
      copy: 'Recent failures requiring follow-up',
      link: { name: 'ConsoleJobs', query: { status: 'failed' } },
      tone: overviewSummary.value.failedJobs > 0 ? 'failed' : 'ready',
    },
    {
      label: 'Runs With Gaps',
      value: jobsWithGaps,
      copy: 'Recent analytics reporting guidance gaps',
      link: { name: 'ConsoleAnalytics' },
      tone: jobsWithGaps > 0 ? 'attention' : 'ready',
    },
    {
      label: 'Runs With Analytics',
      value: jobsWithAnalytics,
      copy: 'Recent runs carrying stored analytics summaries',
      link: { name: 'ConsoleAnalytics' },
      tone: 'active',
    },
  ]
})

const alerts = computed(() => {
  const items = []

  if (operatorAttentionJobs.value > 0) {
    items.push({
      id: 'operator-queue',
      tone: 'warning',
      title: `${operatorAttentionJobs.value} run${operatorAttentionJobs.value === 1 ? ' needs' : 's need'} operator attention`,
      copy: 'There are runs waiting on input, approval, or updated credentials.',
      action: 'Review runs',
      link: { name: 'ConsoleJobs', query: { status: 'attention' } },
    })
  }

  if (overviewSummary.value.failedJobs > 0) {
    items.push({
      id: 'failed-runs',
      tone: 'error',
      title: `${overviewSummary.value.failedJobs} failed run${overviewSummary.value.failedJobs === 1 ? '' : 's'} in the current window`,
      copy: 'Review the most recent failed executions and open the records that need attention.',
      action: 'Inspect failures',
      link: { name: 'ConsoleJobs', query: { status: 'failed' } },
    })
  }

  if (stalePlans.value.length > 0) {
    items.push({
      id: 'stale-plans',
      tone: 'warning',
      title: `${stalePlans.value.length} plan${stalePlans.value.length === 1 ? ' has' : 's have'} been pending for more than 12 hours`,
      copy: 'These plans have sat in the queue long enough to merit operator review.',
      action: 'Open plans',
      link: { name: 'ConsolePlans' },
    })
  }

  if ((analytics.value?.guidance_gaps?.jobs_with_gaps ?? 0) > 0) {
    items.push({
      id: 'guidance-gaps',
      tone: 'neutral',
      title: `${analytics.value.guidance_gaps.jobs_with_gaps} recent run${analytics.value.guidance_gaps.jobs_with_gaps === 1 ? '' : 's'} reported guidance gaps`,
      copy: 'Use analytics to see recurring topics and where evidence or standards support is weak.',
      action: 'Open analytics',
      link: { name: 'ConsoleAnalytics' },
    })
  }

  return items
})

function normalizeWorkspace(item) {
  const workspaceId = item.workspaceId ?? item.id
  const isGithub =
    item.type === 'public-github' || item.type === 'private-github' || item.source === 'github'

  return {
    ...item,
    workspaceId,
    sourceLabel: isGithub ? 'GitHub' : 'Upload',
  }
}

function formatStatusLabel(value) {
  return formatJobStatus(value)
}

function getWorkspaceLabel(workspaceId) {
  if (!workspaceId) return 'No workspace'
  return workspaceMap.value.get(workspaceId) || workspaceId
}

async function fetchOverviewData() {
  const data = await fetchOverviewSummary(requestJson, {
    recentJobLimit: 10,
    analyticsLimit: 20,
  })

  overviewSummary.value = {
    workspaceCount: Number(data.summary?.workspaceCount || 0),
    activeJobs: Number(data.summary?.activeJobs || 0),
    pendingPlans: Number(data.summary?.pendingPlans || 0),
    runningBeads: Number(data.summary?.runningBeads || 0),
    failedJobs: Number(data.summary?.failedJobs || 0),
    workspaces: normalizeOverviewWorkspaceCounts(data.summary?.workspaces),
  }
  recentJobs.value = data.recentJobs
  stalePlans.value = data.stalePlans
  analytics.value = data.analytics
}

function normalizeOverviewWorkspaceCounts(value) {
  const source = value && typeof value === 'object' ? value : {}
  const status = source.byStatus && typeof source.byStatus === 'object' ? source.byStatus : {}
  const bySource = source.bySource && typeof source.bySource === 'object' ? source.bySource : {}
  const num = (n) => (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : 0)
  return {
    total: num(source.total),
    visible: num(source.visible),
    byStatus: {
      creating: num(status.creating),
      ready: num(status.ready),
      failed: num(status.failed),
      archived: num(status.archived),
      deleting: num(status.deleting),
    },
    bySource: {
      github: num(bySource.github),
      upload: num(bySource.upload),
    },
  }
}

async function fetchRecentWorkspaces() {
  const page = await fetchWorkspaceSummaryPage(requestJson, {
    limit: 6,
    includeArchived: true,
  })
  recentWorkspaces.value = page.items.map(normalizeWorkspace)
}

async function fetchWorkspaceLabels() {
  workspaceOptions.value = await fetchWorkspaceOptions(requestJson, { includeArchived: true })
}

async function fetchActiveQueueJobs() {
  activeQueueJobs.value = await fetchAllJobSummaryItems(requestJson, {
    group: 'active',
  })
}

async function loadData({ showLoading = false, background = false } = {}) {
  if (showLoading) loading.value = true
  if (background) refreshBusy.value = true
  error.value = ''
  warnings.value = []

  const [overviewResult, workspacesResult, labelsResult, activeJobsResult] = await Promise.allSettled([
    fetchOverviewData(),
    fetchRecentWorkspaces(),
    fetchWorkspaceLabels(),
    fetchActiveQueueJobs(),
  ])

  if (overviewResult.status === 'rejected') {
    if (!background) {
      overviewSummary.value = {
        workspaceCount: 0,
        activeJobs: 0,
        pendingPlans: 0,
        runningBeads: 0,
        failedJobs: 0,
        workspaces: {
          total: 0,
          visible: 0,
          byStatus: { creating: 0, ready: 0, failed: 0, archived: 0, deleting: 0 },
          bySource: { github: 0, upload: 0 },
        },
      }
      recentJobs.value = []
      stalePlans.value = []
      analytics.value = null
    }
    warnings.value.push('Overview summary could not be loaded.')
    logger.warn('Failed to fetch overview summary', overviewResult.reason)
  }

  if (workspacesResult.status === 'rejected') {
    if (!background) {
      recentWorkspaces.value = []
    }
    warnings.value.push('Recent workspace summaries could not be loaded for the overview.')
    logger.warn('Failed to fetch workspace summaries for overview', workspacesResult.reason)
  }

  if (labelsResult.status === 'rejected') {
    if (!background) {
      workspaceOptions.value = []
    }
    warnings.value.push('Workspace labels could not be loaded for overview run links.')
    logger.warn('Failed to fetch workspace options for overview', labelsResult.reason)
  }

  if (activeJobsResult.status === 'rejected') {
    if (!background) {
      activeQueueJobs.value = []
    }
    warnings.value.push('Operator queue detail could not be loaded for the overview.')
    logger.warn('Failed to fetch active run summaries for overview', activeJobsResult.reason)
  }

  if (!background && overviewResult.status === 'rejected' && workspacesResult.status === 'rejected' && labelsResult.status === 'rejected' && activeJobsResult.status === 'rejected') {
    error.value = 'Failed to load overview.'
  }

  loading.value = false
  refreshBusy.value = false
}

function startRefreshInterval() {
  if (refreshInterval) clearInterval(refreshInterval)
  refreshInterval = setInterval(() => {
    void loadData({ background: true })
  }, REFRESH_INTERVAL_MS)
}

onMounted(() => {
  loadData({ showLoading: true })
  startRefreshInterval()
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
})
</script>

<style scoped>
.overview-alerts {
  display: grid;
  gap: 12px;
  margin-bottom: 20px;
}

.overview-alert-list {
  display: grid;
  gap: 12px;
  margin-bottom: 16px;
}

.overview-alert {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 20px;
  border: 1px solid var(--shell-border);
  border-left: 4px solid var(--shell-accent);
  border-radius: 6px;
  background: var(--shell-surface-strong);
  color: inherit;
  text-decoration: none;
  transition: transform 0.12s, box-shadow 0.12s;
}

.overview-alert:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.overview-alert--attention,
.overview-alert--warning {
  border-left-color: var(--status-attention);
  background: linear-gradient(135deg, var(--status-attention-bg) 0%, transparent 60%);
}

.overview-alert--error {
  border-left-color: var(--status-failed);
  background: linear-gradient(135deg, var(--status-failed-bg) 0%, transparent 60%);
}

.overview-alert--success {
  border-left-color: var(--status-ready);
  background: linear-gradient(135deg, var(--status-ready-bg) 0%, transparent 60%);
}

.overview-alert__title {
  margin: 0;
  font-family: var(--shell-console-font);
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: var(--shell-text);
}

.overview-alert__copy {
  margin: 8px 0 0;
  color: var(--shell-text-muted);
  line-height: 1.6;
}

.overview-alert__action {
  color: var(--shell-accent);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.overview-summary-strip {
  margin-bottom: 16px;
}

.overview-module__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.overview-module__copy,
.overview-empty {
  margin: 0;
  line-height: 1.7;
  color: var(--shell-text-muted);
}

.overview-topic-stream {
  margin-top: 16px;
}

.overview-stream__item {
  border-left: 3px solid transparent;
  padding-left: 12px;
  margin-left: -12px;
  transition: border-color 0.15s, background 0.15s;
}

.overview-stream__item--ready { border-left-color: var(--status-ready); }
.overview-stream__item--active { border-left-color: var(--status-active); }
.overview-stream__item--attention { border-left-color: var(--status-attention); }
.overview-stream__item--pending { border-left-color: var(--status-pending); }
.overview-stream__item--failed { border-left-color: var(--status-failed); }

.overview-stream__time {
  margin: 0;
}

@media (max-width: 768px) {
  .overview-alert,
  .overview-module__header {
    flex-direction: column;
  }
}
</style>
